/**
 * SystemPlaylistFullView — full-page view for system playlists.
 *
 * Serves as the "See Entire Playlist" destination for:
 *   /watch-later        — Watch Later (playlistType='default')
 *   /favorites          — Favorites  (playlistType='favorites')
 *   /currently-watching  — In-progress series
 *   /watch-history       — Watch history
 *
 * Uses Apollo hooks for data fetching (no Redux tiles dependency).
 * Reuses CommunityMyLibraryPage's static text store key.
 */

import React, {
  useState, useCallback, useEffect, useLayoutEffect, useMemo, useRef,
} from 'react'
import { useSelector } from 'react-redux'
import { useQuery, useMutation, useApolloClient } from '@apollo/client'
import { compose } from 'recompose'
import { usePostHogEvent } from '../../../../PostHog/index'
import useOptimisticPlaylistReorder from '../../shared/PlaylistRow/useOptimisticPlaylistReorder'
import type { OptimisticPlaylistReorderCommitArgs } from '../../shared/PlaylistRow/useOptimisticPlaylistReorder'
import { selectAuth } from '../../../../../services/auth/selectors'
import { connect as connectStaticText } from '../../../../StaticText/connect'
import { StaticTextProvider, useTranslatedText } from '../../../../StaticText/StaticTextContext'
import { connect as connectPage, PAGE_SEO_TYPE_LOCATION } from '../../../../Page/connect'
import BackButton from '../../../../BackButton/index'
import Button, { TYPES as BUTTON_TYPES } from '../../../../Button/index'
import { H2 } from '../../../../Heading/index'
import Loading, { LOADING_ICON_BLUE_DARK_BG_WHITE } from '../../../../Loading/index'
import Pencil from '../../../../Icon/icons/Pencil/index'
import Check from '../../../../Icon/icons/Check/index'
import { InfiniteScroll } from '../../../../OraclePage/InfiniteScroll'
import {
  PlaylistTileGrid,
  PlaylistGridSkeleton,
} from '../../shared/CommunityProfileCarousels/index'
import type {
  CarouselItem,
  EditToolbarLabels,
} from '../../shared/CommunityProfileCarousels/index'
import withCommunityErrorBoundary from '../../../shared/ErrorHandling/withCommunityErrorBoundary/index'
import useCommunityEnabled from '../../../../../services/community/shared/useCommunityEnabled'
import useRequireAuth from '../../../../../services/community/shared/useRequireAuth'
import usePrimaryAppLanguage from '../../../../../services/community/shared/usePrimaryAppLanguage'
import useIsMobile from '../../../../../services/community/shared/useIsMobile'
import {
  MobileBreakpointProvider,
  COMMUNITY_BREAKPOINTS,
} from '../../../../../services/community/shared/MobileBreakpointContext'
import useInProgressEnrichment from '../../../../../services/community/identity/library/useInProgressEnrichment'
import useWatchHistoryData from '../../../../../services/community/identity/library/useWatchHistoryData'
import useResolveOwnedPlaylistId from '../../../../../services/community/identity/library/useResolveOwnedPlaylistId'
import { enrichPlaylistItems } from '../../../../../services/community/identity/library/content-utils'
import useLatest from '../../../../../services/community/shared/use-latest'
import {
  GET_IN_PROGRESS_FULL,
  GET_PLAYLISTS_WITH_ITEMS,
  REMOVE_PLAYLIST_ITEM,
  UPDATE_PLAYLIST_ITEM,
} from '../../../../../services/community/query'
import { FLAG_WATCHED_VIDEO } from '../../../../../services/playlist/query'
import {
  createRemoveItemUpdater,
  createReorderPlaylistItemsUpdater,
} from '../../../../../services/community/shared/cache-updaters'
import {
  APOLLO_CLIENT_FETCH_POLICY_CACHE_AND_NETWORK,
  APOLLO_CLIENT_ERROR_POLICY_ALL,
} from '../../../../../services/apollo-graphql/index'

import type {
  GetInProgressFullResponse,
  GetMyPlaylistsResponse,
  Playlist,
  EnrichedPlaylistItem,
  EnrichedInProgressItem,
  StaticTextMap,
} from '../../../../../services/community/types'

// Route config — maps pathname to data source + i18n keys
type DataSource = 'playlist' | 'inProgress' | 'watchHistory'

interface RouteConfig {
  playlistType?: string
  /** Optional for custom playlists — title comes from playlistName instead */
  titleKey?: string
  emptyTextKey: string
  dataSource: DataSource
  /** Present when the pathname is `/playlist/<uuid>` (custom playlist full view) */
  customPlaylistId?: string
}

const ROUTE_CONFIGS: Record<string, RouteConfig> = {
  '/watch-later': {
    playlistType: 'default',
    titleKey: 'watchLater',
    emptyTextKey: 'emptyWatchLater',
    dataSource: 'playlist',
  },
  '/favorites': {
    playlistType: 'favorites',
    titleKey: 'favorites',
    emptyTextKey: 'emptyFavorites',
    dataSource: 'playlist',
  },
  '/currently-watching': {
    titleKey: 'currentlyWatching',
    emptyTextKey: 'emptyCurrentlyWatching',
    dataSource: 'inProgress',
  },
  '/watch-history': {
    titleKey: 'watchHistory',
    emptyTextKey: 'emptyWatchHistory',
    dataSource: 'watchHistory',
  },
}

/** Matches `/playlist/<uuid>` — the custom playlist full-view route.
 *  Case-insensitive to align with path-to-regexp's default route matching
 *  (see `lib/services/resolver/match-path.js`). */
const CUSTOM_PLAYLIST_PATH_RE = /^\/playlist\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i

function getRouteConfig (pathname: string): RouteConfig {
  const customMatch = pathname.match(CUSTOM_PLAYLIST_PATH_RE)
  if (customMatch) {
    return {
      dataSource: 'playlist',
      emptyTextKey: 'noEpisodesYet',
      customPlaylistId: customMatch[1],
    }
  }
  return ROUTE_CONFIGS[pathname] || ROUTE_CONFIGS['/watch-later']
}

// Watch history page size for load-more
const WATCH_HISTORY_PAGE_SIZE = 24

// DOM-windowing batch size for the in-memory grid. Both the in-progress and
// playlist full-views fetch all items up front, then reveal tiles in batches
// of this size as the user scrolls (see `windowed` / handleWindowLoadMore).
const GRID_PAGE = 24

// Edge auto-scroll speed (px/frame) for drag-reorder on this full-page grid.
// Tuned for instant (non-smooth) scrolling at display refresh: at ~60-120fps,
// 30px/frame is ~1800-3600px/s — fast enough to traverse a long playlist while
// staying controllable. Pairs with the base-velocity floor + 120px band below.
const FULL_VIEW_AUTO_SCROLL_MAX_STEP_PX = 30
// Wider trigger band (px) than the engine's default 48 so the auto-scroll engages
// well before the cursor reaches the very edge — the dragged tile is large, so a
// narrow band leaves almost no room to trigger it.
const FULL_VIEW_AUTO_SCROLL_EDGE_PX = 120

// Inner component (receives staticText from HOC)
interface SystemPlaylistFullViewInnerProps {
  location: { pathname: string }
  history: { push: (path: string) => void }
  staticText: StaticTextMap
}

// Inner component: lives inside the Outer's <StaticTextProvider> (set up by
// SystemPlaylistFullViewContent below). The `staticText` prop is no longer
// passed in — UI copy is resolved via useTranslatedText hooks against the
// surrounding provider.
function SystemPlaylistFullViewContentInner ({
  location,
}: Omit<SystemPlaylistFullViewInnerProps, 'staticText'>) {
  const { enabled, loading: communityLoading } = useCommunityEnabled()
  const { isLoggedIn } = useRequireAuth()
  const language = usePrimaryAppLanguage()
  const auth = useSelector(selectAuth)
  const jwt = (auth.get('jwt') as string) || ''
  const viewerUuid = (auth.get('uuid') as string) || ''
  const apolloClient = useApolloClient()

  const [editMode, setEditMode] = useState(false)
  // Latest-value ref read only inside the toggle callback (post-commit), so the
  // effect-phase write in `useLatest` is safe and Suspense-correct.
  const editModeRef = useLatest(editMode)
  // In-progress and playlist routes both window the in-memory grid in DOM batches.
  const [renderCount, setRenderCount] = useState(GRID_PAGE)
  const [windowExpanding, setWindowExpanding] = useState(false)
  const windowExpandingRef = useRef(false)
  const [prevPathname, setPrevPathname] = useState(location.pathname)
  if (location.pathname !== prevPathname) {
    setPrevPathname(location.pathname)
    setRenderCount(GRID_PAGE)
  }
  const handleToggleEditMode = useCallback(() => {
    const enteringEdit = !editModeRef.current
    setEditMode(!editModeRef.current)
    if (enteringEdit) setRenderCount(GRID_PAGE)
  }, [])

  // Toolbar size scales with the design system's primary breakpoints so the
  // pills + icons match the surrounding tile size at each viewport tier:
  //   < 768px (Mobile)  → small  (26×26 / 18px) — same as carousel-row default
  //   768–1279 (Tablet) → medium (32×32 / 22px)
  //   ≥ 1280  (Desktop) → large  (36×36 / 24px)
  // Inline icon `size` is set via React prop (no CSS-`!important` workaround
  // exists), so we drive the variant from JS via two `useIsMobile` calls.
  const isMobileBp = useIsMobile(768) // < 768px
  const isTabletOrBelow = useIsMobile(1280) // < 1280px
  let toolbarSize: 'small' | 'medium' | 'large' = 'large'
  if (isMobileBp) toolbarSize = 'small'
  else if (isTabletOrBelow) toolbarSize = 'medium'

  const config = getRouteConfig(location.pathname)
  const isPlaylistRoute = config.dataSource === 'playlist'
  const isInProgressRoute = config.dataSource === 'inProgress'
  const isWatchHistoryRoute = config.dataSource === 'watchHistory'

  // ── Playlist data (Watch Later / Favorites / custom) ────────────────
  // Cache-first id resolution (useResolveOwnedPlaylistId) gives us the owned
  // playlist's UUID with ownership gating. We then fetch the WHOLE playlist in
  // one shot (GET_PLAYLISTS_WITH_ITEMS → server `playlistList.playlistItems`)
  // and window the resulting tiles in the DOM (see `windowed` below). The
  // paginated `playlistItemList` resolver under-returns vs. the whole-playlist
  // resolver, so a single whole fetch is the correct/adequate count source;
  // freeze is handled by DOM windowing, not network pagination.
  const {
    targetId,
    resolving: metaResolving,
    resolvedPlaylistName,
  } = useResolveOwnedPlaylistId({
    enabled: isPlaylistRoute,
    isLoggedIn,
    playlistType: config.playlistType,
    customPlaylistId: config.customPlaylistId,
    jwt,
  })

  const {
    data: playlistsData,
    loading: itemsLoading,
    refetch: refetchPlaylistItems,
  } = useQuery<GetMyPlaylistsResponse>(GET_PLAYLISTS_WITH_ITEMS, {
    variables: { playlistIds: targetId ? [targetId] : [], language: [language, '_primary'] },
    skip: !isPlaylistRoute || !isLoggedIn || !targetId,
    fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_CACHE_AND_NETWORK,
    errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
    context: { jwt },
  })

  const playlistsLoading = metaResolving || (!!targetId && itemsLoading)

  const targetPlaylist = useMemo(() => {
    if (!isPlaylistRoute || !targetId || !playlistsData) return null
    return (playlistsData.playlistList?.playlists || [])
      .find((pl: Playlist) => pl.id === targetId) || null
  }, [isPlaylistRoute, targetId, playlistsData])

  const playlistItems = useMemo(() => {
    if (!targetPlaylist) return []
    return enrichPlaylistItems(targetPlaylist.playlistItems || [], {
      dedupe: false,
      pivotEpisodesToSeries: false,
      requireNavigablePath: true,
    })
  }, [targetPlaylist])

  const reorderRefetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const refetchPlaylistItemsRef = useLatest(refetchPlaylistItems)
  const scheduleReorderRefetch = useCallback(() => {
    if (reorderRefetchTimerRef.current) {
      clearTimeout(reorderRefetchTimerRef.current)
    }
    reorderRefetchTimerRef.current = setTimeout(() => {
      reorderRefetchTimerRef.current = null
      refetchPlaylistItemsRef.current()
    }, 250)
  }, [refetchPlaylistItemsRef])

  useEffect(() => () => {
    if (reorderRefetchTimerRef.current) {
      clearTimeout(reorderRefetchTimerRef.current)
    }
  }, [])

  const [removePlaylistItemMutation] = useMutation(REMOVE_PLAYLIST_ITEM, {
    context: { jwt },
  })

  const [updatePlaylistItemMutation] = useMutation(UPDATE_PLAYLIST_ITEM, {
    context: { jwt },
  })

  const commitReorder = useCallback(async ({
    moving,
    destPosition,
    fromIndex,
    toIndex,
    visibleItems,
  }: OptimisticPlaylistReorderCommitArgs<EnrichedPlaylistItem>) => {
    if (!targetPlaylist) return
    const contentId = moving.playlistItemContentId
    const contentType = moving.playlistItemContentType || 'video'
    const nextList = [...visibleItems]
    nextList.splice(fromIndex, 1)
    nextList.splice(toIndex, 0, moving)
    const orderedItemIds = nextList.map((item) => String(item.id))
    const playlistEntityId = apolloClient.cache.identify({
      __typename: 'Playlist',
      id: targetPlaylist.id,
    })
    await updatePlaylistItemMutation({
      variables: {
        playlistItemId: moving.id,
        contentId,
        contentType,
        playlistPosition: destPosition,
        playlistId: targetPlaylist.id,
        playlistType: targetPlaylist.playlistType,
      },
      update: (cache) => {
        createReorderPlaylistItemsUpdater({
          entityId: playlistEntityId,
          field: 'playlistItems',
          orderedItemIds,
        })(cache)
      },
    })
    scheduleReorderRefetch()
  }, [
    targetPlaylist,
    updatePlaylistItemMutation,
    scheduleReorderRefetch,
    apolloClient,
  ])

  const {
    visibleItems: reorderVisibleItems,
    handleReorder: handleReorderPlaylistItem,
    reorderInFlight,
  } = useOptimisticPlaylistReorder({
    baseItems: playlistItems,
    resetDeps: [location.pathname, editMode],
    clearMode: 'immediate',
    validateItems: (moving) => Boolean(moving.playlistItemContentId),
    commit: commitReorder,
  })

  const handleRemovePlaylistItem = useCallback(async (item: CarouselItem) => {
    if (!targetPlaylist) return
    const enrichedItem = item as EnrichedPlaylistItem
    // Use the playlist-item ROW's own contentId / contentType — these are
    // the values the backend matches against. Reading from the tile or the
    // resolved content fails for series-pivoted tiles (`'series'`) and
    // gaia originals (`'gaiaOriginal'`), neither of which matches the
    // row's stored `content_type`.
    const contentId = enrichedItem.playlistItemContentId
    const contentType = enrichedItem.playlistItemContentType || 'video'
    const playlistItemId = enrichedItem.id
    if (!contentId || !playlistItemId) return
    try {
      // Cache-correct remove via the shared updater: filters the targeted
      // Playlist entity's `playlistItems` array and decrements `itemCount`
      // in the same modify() pass.  Preserves scroll position past page 1
      // and avoids the multi-refetch amplification a multi-select would
      // otherwise produce.  Reorder still uses refetchPlaylists() because
      // server ordering is authoritative.
      await removePlaylistItemMutation({
        variables: {
          contentId,
          contentType,
          playlistId: targetPlaylist.id,
          playlistType: targetPlaylist.playlistType,
        },
        update: (cache) => {
          createRemoveItemUpdater({
            entityId: cache.identify({ __typename: 'Playlist', id: targetPlaylist.id }),
            field: 'playlistItems',
            countField: 'itemCount',
            countLocation: 'sibling',
            match: (ref, readField) => String(readField('id', ref)) === String(playlistItemId),
          })(cache)
        },
      })
    } catch {
      // mutation failed — item stays visible
    }
  }, [targetPlaylist, removePlaylistItemMutation])

  // ── In-progress data (Currently Watching) ───────────────────────────
  // Uses the inProgress federated field (same data source as the profile
  // carousel row) with limit 24. This field includes hiddenFromContinue
  // which useInProgressEnrichment needs for proper client-side filtering.
  const {
    data: inProgressData,
    loading: inProgressLoading,
  } = useQuery<GetInProgressFullResponse>(GET_IN_PROGRESS_FULL, {
    variables: { language, contentLanguage: [language, '_primary'] },
    skip: !isInProgressRoute || !isLoggedIn,
    fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_CACHE_AND_NETWORK,
    errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
    context: { jwt },
  })

  const rawInProgress = isInProgressRoute
    ? inProgressData?.userProfileCurrent?.inProgress
    : undefined
  // language + jwt thread into useInProgressEnrichment's internal
  // useTitleGroupHydration call so the difficulty pill renders on this
  // surface — PR 2 dropped `featureMedia.mediaAssets.aiMetadata.learnerLevel`
  // from GET_IN_PROGRESS_FULL; hydration restores it.
  // The hook returns `{ items, isHydrating }` — this full-view surface
  // doesn't surface skeleton state today (the page-level loading gates the
  // shell), so we only consume `items`. Add `isHydrating` here if/when
  // this view grows a per-row skeleton path.
  const { items: inProgressItems } = useInProgressEnrichment(rawInProgress, language, jwt)

  const [flagWatchedVideoMutation] = useMutation(FLAG_WATCHED_VIDEO, {
    context: { jwt },
  })

  const handleRemoveInProgressItem = useCallback(async (item: CarouselItem) => {
    // Bundle fan-out: a series-pivoted Currently Watching tile collapses N
    // raw in-progress episode rows into one visible tile (see
    // `useInProgressEnrichment.ts` → the `accIndexByTileKey` bundling
    // reduce, which pushes onto `bundledEpisodeContentIds`).
    // Removing must hide EVERY
    // bundled episode — both server-side (N `flagWatchedVideo` mutations)
    // and in the cache (drop every matching `UserProfile.inProgress`
    // entry) — otherwise the leftover episodes re-pivot into the same
    // series tile on the next render and the user sees Remove fail to
    // stick.  Standalone videos arrive with a single-element bundle, so
    // the same code path covers both shapes uniformly.
    const enriched = item as EnrichedInProgressItem
    const fallbackContentId = Number(item.contentId)
    const enrichedContentIds = enriched.bundledEpisodeContentIds
    let bundleContentIds: number[]
    if (enrichedContentIds && enrichedContentIds.length > 0) {
      bundleContentIds = enrichedContentIds.filter((id) => Number.isFinite(id) && id > 0)
    } else if (Number.isFinite(fallbackContentId) && fallbackContentId > 0) {
      bundleContentIds = [fallbackContentId]
    } else {
      bundleContentIds = []
    }
    const fallbackMediaId = enriched.mediaId ?? enriched.id
    const enrichedMediaIds = enriched.bundledEpisodeMediaIds
    let bundleMediaIds: string[]
    if (enrichedMediaIds && enrichedMediaIds.length > 0) {
      bundleMediaIds = enrichedMediaIds
    } else if (fallbackMediaId != null) {
      bundleMediaIds = [String(fallbackMediaId)]
    } else {
      bundleMediaIds = []
    }
    if (bundleContentIds.length === 0) return

    // Fire one flagWatchedVideo per bundled episode in parallel.
    // `Promise.allSettled` so a single resolver hiccup on a tail episode
    // doesn't strand the rest — the user expects the Remove click to
    // make a best-effort attempt at hiding everything.
    const results = await Promise.allSettled(
      bundleContentIds.map((videoId) => flagWatchedVideoMutation({
        variables: { videoId, hiddenByUser: true },
      })),
    )
    const succeeded = results.filter((r) => r.status === 'fulfilled').length
    // Every mutation rejected → keep the tile visible (fail-quiet contract
    // matching the prior single-mutation behavior). Partial success still
    // commits the cache update so the user's optimistic UI reflects what
    // did succeed.
    if (succeeded === 0) return

    if (!viewerUuid) return
    const profileId = apolloClient.cache.identify({ __typename: 'UserProfile', uuid: viewerUuid })
    if (!profileId) return
    const bundleMediaIdSet = new Set(bundleMediaIds.map(String))
    createRemoveItemUpdater({
      entityId: profileId,
      field: 'inProgress',
      match: (ref, readField) => bundleMediaIdSet.has(String(readField('mediaId', ref))),
    })(apolloClient.cache)
  }, [flagWatchedVideoMutation, viewerUuid, apolloClient])

  // ── Watch History data ──────────────────────────────────────────────
  const {
    watchHistoryItems,
    watchHistoryLoading,
    onRemoveWatchHistoryItem,
    loadMore,
    lastBatchSize,
    isLoadingMore,
  } = useWatchHistoryData({
    language,
    jwt,
    isLoggedIn: isLoggedIn && isWatchHistoryRoute,
    limit: WATCH_HISTORY_PAGE_SIZE,
  })

  const handleRemoveWatchHistoryItem = useCallback(async (item: CarouselItem) => {
    await onRemoveWatchHistoryItem(item as EnrichedPlaylistItem)
  }, [onRemoveWatchHistoryItem])

  // ── Page-view tracking (PostHog session-recording trigger) ─────────────
  // Fires once after the route resolves and (for playlist routes) the data
  // query settles enough to expose a stable identifier.  A ref guard makes
  // this resilient to React strict-mode and Apollo cache updates.
  const trackPlaylistViewed = usePostHogEvent('community_playlist_viewed')
  const hasTrackedPlaylistView = useRef(false)
  const playlistId = config.customPlaylistId
    || config.playlistType
    || config.dataSource
  const playlistType: 'custom' | 'system' = config.customPlaylistId ? 'custom' : 'system'
  // System playlists are always the viewer's own. A custom playlist's `targetId`
  // is resolved ONLY from the `userCurrent` metadata query (useResolveOwnedPlaylistId)
  // — deliberately NOT from a direct `Playlist:<id>` cache read, which a public
  // shared-view visit can populate for a non-owned playlist. So a resolved
  // `targetPlaylist` here proves the viewer owns it (and gates edit/delete/reorder
  // plus the `isOwner` analytics dimension below).
  const isOwner = playlistType === 'system'
    ? true
    : !!targetPlaylist
  useEffect(() => {
    if (hasTrackedPlaylistView.current) return
    if (!isLoggedIn) return
    if (!playlistId) return
    // For custom playlists wait until the lookup settles so we don't fire
    // with an unresolved owner state.
    if (playlistType === 'custom' && playlistsLoading) return
    hasTrackedPlaylistView.current = true
    trackPlaylistViewed(playlistId, playlistType, isOwner)
  }, [isLoggedIn, playlistId, playlistType, isOwner, playlistsLoading, trackPlaylistViewed])

  // ── Unified items + loading + remove handler ────────────────────────
  let items: CarouselItem[] = []
  let dataLoading = false
  // Returns void | Promise<void>: the carousel awaits this so it can show a
  // ghost-tile placeholder until the mutation+refetch resolves.
  let onRemoveItem: ((item: CarouselItem) => void | Promise<void>) | undefined

  if (isPlaylistRoute) {
    // Prefer the optimistic local order if a reorder is in flight; falls
    // back to the server-sorted enriched items otherwise.
    items = reorderVisibleItems
    dataLoading = playlistsLoading
    onRemoveItem = handleRemovePlaylistItem
  } else if (isInProgressRoute) {
    items = inProgressItems
    dataLoading = inProgressLoading
    onRemoveItem = handleRemoveInProgressItem
  } else if (isWatchHistoryRoute) {
    items = watchHistoryItems
    dataLoading = watchHistoryLoading
    onRemoveItem = handleRemoveWatchHistoryItem
  }

  // Suppress stale tiles when the items query hasn't caught up to a new
  // targetId (fast route switches between owned playlists). `targetPlaylist` is
  // derived by matching `pl.id === targetId`, so its absence is the only stale
  // signal — an id mismatch can't occur.
  const itemsStale = isPlaylistRoute && !!targetId && !targetPlaylist
  const displayItems = itemsStale ? [] : items

  // Window the in-memory grid for in-progress AND playlist routes. The whole
  // playlist is fetched in one shot, then tiles are revealed in DOM batches as
  // the user scrolls. Edit mode renders all tiles so drag-reorder indices align
  // with the full list. Watch history uses its own network InfiniteScroll below.
  const windowed = (isInProgressRoute || isPlaylistRoute)
    && !editMode
    && displayItems.length > renderCount
  const renderedGridCount = windowed ? renderCount : displayItems.length

  // Latest-value ref read only inside the load-more callback (post-commit).
  const itemsCountRef = useLatest(displayItems.length)
  const handleWindowLoadMore = useCallback(() => {
    if (windowExpandingRef.current) return
    windowExpandingRef.current = true
    setWindowExpanding(true)
    setRenderCount((c) => Math.min(c + GRID_PAGE, itemsCountRef.current))
  }, [])

  useLayoutEffect(() => {
    windowExpandingRef.current = false
    if (windowExpanding) setWindowExpanding(false)
  }, [renderCount, windowExpanding])

  // ── i18n strings ────────────────────────────────────────────────────
  // Hooks resolve against the surrounding <StaticTextProvider> installed by
  // the Outer wrapper (SystemPlaylistFullViewContent below). Title and empty-
  // text keys are dynamic per route — the hook accepts a derived key string
  // just fine because each call site is a single, stable hook invocation.
  // NOTE: these run BEFORE the gate checks below so the hook count stays
  // stable when a logged-out user logs in via the in-place overlay (flipping
  // the gate would otherwise change the hook count and crash React).
  const playlistFallbackLabel = useTranslatedText({ keys: ['playlist'] })
  const titleFromKey = useTranslatedText({ keys: [config.titleKey || ''] })
  const emptyText = useTranslatedText({ keys: [config.emptyTextKey] })
  const backLabel = useTranslatedText({ keys: ['backToLibrary'] })
  const editPlaylistLabel = useTranslatedText({ keys: ['editPlaylistLabel'] })
  const doneEditingLabel = useTranslatedText({ keys: ['doneEditingLabel'] })
  const earlierLabel = useTranslatedText({ keys: ['moveEarlierInPlaylist'] })
  const laterLabel = useTranslatedText({ keys: ['moveLaterInPlaylist'] })
  const removeLabel = useTranslatedText({ keys: ['removeFromPlaylist'] })
  const confirmLabel = useTranslatedText({ keys: ['confirmRemoveFromPlaylist'] })
  const dragRoleDescriptionLabel = useTranslatedText({ keys: ['dragRoleDescription'] })
  const dragLiftedLabel = useTranslatedText({ keys: ['dragLifted'] })
  const dragGrabbedLabel = useTranslatedText({ keys: ['dragGrabbed'] })
  const dragMovedInPlaylistLabel = useTranslatedText({ keys: ['dragMovedInPlaylist'] })
  const dragDroppedLabel = useTranslatedText({ keys: ['dragDropped'] })
  const dragCancelledLabel = useTranslatedText({ keys: ['dragCancelled'] })

  // ── Gate checks ─────────────────────────────────────────────────────
  if (communityLoading) {
    return <Loading visible icon={LOADING_ICON_BLUE_DARK_BG_WHITE} />
  }
  if (!enabled || !isLoggedIn) {
    return null
  }

  // Custom playlists render their user-authored name; system playlists use
  // a fixed staticText title keyed by pathname.
  let title: string
  if (config.customPlaylistId) {
    title = resolvedPlaylistName || playlistFallbackLabel
  } else {
    title = titleFromKey
  }

  // Edit-toolbar i18n labels resolved once at the parent and threaded down
  // to every reorder/remove pill in the grid.
  const editToolbarLabels: EditToolbarLabels = {
    earlierLabel,
    laterLabel,
    removeLabel,
    confirmLabel,
    dragRoleDescription: dragRoleDescriptionLabel,
    dragLifted: dragLiftedLabel,
    dragGrabbed: dragGrabbedLabel,
    dragMovedInPlaylist: dragMovedInPlaylistLabel,
    dragDropped: dragDroppedLabel,
    dragCancelled: dragCancelledLabel,
  }

  const backUrl = '/playlist/default'

  return (
    <div className="system-playlist-full-view">
        <div className="system-playlist-full-view__header">
          <div className="system-playlist-full-view__back">
            <BackButton
              className="system-playlist-full-view__back-button"
              url={backUrl}
            >
              {backLabel}
            </BackButton>
          </div>
          <H2 className="system-playlist-full-view__title">{title}</H2>
          <div className="system-playlist-full-view__edit-action">
            {displayItems.length > 0 ? (
              <Button
                type={BUTTON_TYPES.GHOST}
                className="system-playlist-full-view__edit-button"
                onClick={handleToggleEditMode}
                aria-label={editMode ? doneEditingLabel : editPlaylistLabel}
              >
                {editMode ? <Check /> : <Pencil />}
              </Button>
            ) : null}
          </div>
        </div>
        {/* Load-bearing: this `items.length === 0` gate is what keeps the
            non-`loading && !data` `playlistsLoading` binding flash-free
            on warm-cache revisits — cached tiles paint instead of this skeleton.
            Don't relax it to `dataLoading` alone. */}
        {dataLoading && displayItems.length === 0 ? (
          // Tile-shaped skeleton instead of the generic spinner so the
          // transition into <PlaylistTileGrid> is reflow-free.  Eight
          // cells fill the visible viewport at the most common widths
          // (4 × 2 desktop, 3 × ~3 tablet, 2 × 4 mobile).  The full view
          // is grid-only at every viewport, so we use the grid skeleton
          // directly rather than the responsive carousel one.
          //
          // Series-label corner-badge placeholder is opt-in via
          // `showSeriesLabel` and only enabled on the Currently Watching
          // full view (`isInProgressRoute`), where almost every tile is a
          // series rollup.  Watch Later / Favorites / Watch History /
          // custom playlists rarely render series labels, so we keep the
          // skeleton badge-free on those routes to avoid the flash-then-
          // disappear on hand-off.
          <PlaylistGridSkeleton count={8} showSeriesLabel={isInProgressRoute} />
        ) : null}
        {displayItems.length > 0 ? (
          <PlaylistTileGrid
            key={targetId ?? location.pathname}
            items={displayItems}
            initialCount={renderedGridCount}
            seeMoreLabel=""
            // Keep the grid's own See-more/Show-less toggle suppressed — reveal
            // is driven by the <InfiniteScroll> below, not the inline toggle.
            hideToggle
            editMode={editMode}
            onRemoveItem={onRemoveItem}
            // Reorder is only meaningful on owned-playlist routes — Currently
            // Watching and Watch History are server-ordered, not user-orderable.
            onReorderItem={isPlaylistRoute ? handleReorderPlaylistItem : undefined}
            // Lock all reorder chevrons while a single move is mutating +
            // refetching.  Prevents users from queueing multiple moves whose
            // mutations would race against stale `playlistPosition` values.
            reorderDisabled={reorderInFlight}
            // Toolbar pill + icon size scales with the viewport: small on mobile,
            // medium on tablet, large on desktop.  Computed at the top of this
            // component from `useIsMobile` against 768/1280 breakpoints.
            editToolbarSize={toolbarSize}
            editToolbarLabels={editToolbarLabels}
            // Faster edge auto-scroll than the default — this is a long single
            // scroll surface, so dragging to the top/bottom on a big playlist
            // needs to travel quickly, and over a wider band so it engages
            // before the cursor reaches the very edge.
            autoScrollMaxStepPx={FULL_VIEW_AUTO_SCROLL_MAX_STEP_PX}
            autoScrollEdgePx={FULL_VIEW_AUTO_SCROLL_EDGE_PX}
          />
        ) : null}
        {windowed ? (
          <InfiniteScroll
            onLoadMore={handleWindowLoadMore}
            currentCount={renderCount}
            lastBatchSize={GRID_PAGE}
            limit={GRID_PAGE}
            loading={windowExpanding}
          />
        ) : null}
        {isWatchHistoryRoute ? (
          <>
            <InfiniteScroll
              onLoadMore={loadMore}
              currentCount={items.length}
              lastBatchSize={lastBatchSize}
              limit={WATCH_HISTORY_PAGE_SIZE}
              loading={isLoadingMore}
            />
            {/*
              InfiniteScroll itself renders only a hidden intersection-observer
              div — no visible affordance.  Surface an explicit Loading spinner
              while the fetchMore round-trip (200–800ms) is in flight so a user
              scrolling past page 1 doesn't conclude the list is exhausted.
              `data-infinite-scroll-spinner` is a regression-guard probe.
            */}
            {isLoadingMore ? (
              <div
                className="system-playlist-full-view__loading-more"
                data-infinite-scroll-spinner="true"
              >
                <Loading visible icon={LOADING_ICON_BLUE_DARK_BG_WHITE} />
              </div>
            ) : null}
          </>
        ) : null}
        {/* Load-bearing: the `items.length === 0` half of this gate keeps the
            non-`loading && !data` `playlistsLoading` binding flash-free
            — a warm-cache revisit has items, so the empty state is suppressed.
            Don't drop the length check. */}
        {!dataLoading && displayItems.length === 0 ? (
          <div className="system-playlist-full-view__empty">
            <p>{emptyText}</p>
          </div>
        ) : null}
      </div>
  )
}

// Outer wrapper — installs the <StaticTextProvider> around Inner so its
// useTranslatedText hooks resolve against the `communityMyLibraryPage` slice
// the HOC injects. Mirrors the Oracle pattern (PlaylistSharedView →
// PlaylistSharedViewInner).
function SystemPlaylistFullViewContent ({
  staticText,
  ...rest
}: SystemPlaylistFullViewInnerProps) {
  return (
    <MobileBreakpointProvider breakpoints={COMMUNITY_BREAKPOINTS}>
      <StaticTextProvider staticText={staticText}>
        <SystemPlaylistFullViewContentInner {...rest} />
      </StaticTextProvider>
    </MobileBreakpointProvider>
  )
}

// HOC composition — staticText + page SEO (no Redux tiles, no languageConnect)
const ConnectedContent = compose(
  connectStaticText({ storeKey: 'communityMyLibraryPage' }),
  connectPage({
    seoType: PAGE_SEO_TYPE_LOCATION,
    storeBranch: '',
    storeKey: 'communityMyLibraryPage',
  }),
)(SystemPlaylistFullViewContent as React.ComponentType<unknown>) as React.ComponentType<
  Omit<SystemPlaylistFullViewInnerProps, 'staticText'>
>

// Outer export
interface SystemPlaylistFullViewProps {
  history: { push: (path: string) => void }
  location: { pathname: string }
}

function SystemPlaylistFullView (
  props: SystemPlaylistFullViewProps,
) {
  return <ConnectedContent {...props} />
}

// withCommunityErrorBoundary derives its NR `source` from this; without an
// explicit displayName the prod build reports a terser-mangled function name.
SystemPlaylistFullView.displayName = 'SystemPlaylistFullView'

export default withCommunityErrorBoundary(SystemPlaylistFullView)
